import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import linkstate from 'linkstate';

import DragNDropFileupload from '../../components/form/dragNdropFileupload';

// Fields on Gjensidige's direkteoppgjørsskjema (BL13_0187). The backend
// prefills them from the sale; the vet reviews/adjusts before sending.
// Read-only definition — never mutate.
const SKJEMA_FIELDS = [
    { name: 'klinikk', label: <Text id='dyrejournal.send-insurance-claim.skjema-klinikk'>Klinikk</Text> },
    { name: 'klinikkOrgNr', label: <Text id='dyrejournal.send-insurance-claim.skjema-orgnr'>Klinikkens org.nr</Text> },
    { name: 'kontonummer', label: <Text id='dyrejournal.send-insurance-claim.skjema-kontonummer'>Kontonummer (for utbetaling)</Text> },
    { name: 'kundeNavn', label: <Text id='dyrejournal.send-insurance-claim.skjema-kundenavn'>Eiers navn</Text> },
    { name: 'kundeFodselsdato', label: <Text id='dyrejournal.send-insurance-claim.skjema-kundefodselsdato'>Eiers fødselsdato</Text> },
    { name: 'kundeEpost', label: <Text id='dyrejournal.send-insurance-claim.skjema-kundeepost'>Eiers e-postadresse</Text> },
    { name: 'forsikringsnummer', label: <Text id='dyrejournal.send-insurance-claim.skjema-forsikringsnummer'>Forsikringsnummer</Text> },
    { name: 'dyr', label: <Text id='dyrejournal.send-insurance-claim.skjema-dyr'>Dyr (hund, katt etc.)</Text> },
    { name: 'rase', label: <Text id='dyrejournal.send-insurance-claim.skjema-rase'>Rase</Text> },
    { name: 'dyretsNavn', label: <Text id='dyrejournal.send-insurance-claim.skjema-dyretsnavn'>Dyrets navn</Text> },
    { name: 'dyretsFodselsdato', label: <Text id='dyrejournal.send-insurance-claim.skjema-dyretsfodselsdato'>Dyrets fødselsdato</Text> },
    { name: 'diagnose', wide: true, label: <Text id='dyrejournal.send-insurance-claim.skjema-diagnose'>Diagnose (kun den diagnosen kravet gjelder)</Text> },
    { name: 'skadeDato', label: <Text id='dyrejournal.send-insurance-claim.skjema-skadedato'>Når ble ulykken/sykdommen oppdaget?</Text> },
    { name: 'flereDiagnoser', type: 'janei', label: <Text id='dyrejournal.send-insurance-claim.skjema-flerediagnoser'>Gjelder kravet flere enn én diagnose?</Text> },
    { name: 'avlivet', type: 'janei', label: <Text id='dyrejournal.send-insurance-claim.skjema-avlivet'>Gjelder kravet avliving?</Text> },
    { name: 'sterilisering', type: 'janei', label: <Text id='dyrejournal.send-insurance-claim.skjema-sterilisering'>Gjelder kravet sterilisering/kastrering?</Text> },
    { name: 'fakturanr', label: <Text id='dyrejournal.send-insurance-claim.skjema-fakturanr'>Fakturanr./Journalnr.</Text> },
    { name: 'kidnr', label: <Text id='dyrejournal.send-insurance-claim.skjema-kidnr'>KIDnr. (kun ved utbetaling med KID)</Text> },
    { name: 'totalForBesok', label: <Text id='dyrejournal.send-insurance-claim.skjema-totalforbesok'>Total for besøk (eks. mva)</Text> },
    { name: 'andelSkade', label: <Text id='dyrejournal.send-insurance-claim.skjema-andelskade'>Utgifter relatert til ulykke/sykdom (eks. mva)</Text> },
    { name: 'andelCtMri', label: <Text id='dyrejournal.send-insurance-claim.skjema-andelctmri'>Av dette CT/MRI (eks. mva)</Text> },
    { name: 'andelRehab', label: <Text id='dyrejournal.send-insurance-claim.skjema-andelrehab'>Av dette rehabilitering (eks. mva)</Text> },
];

// Agria assigns each clinic an ombudsnummer for direkteoppgjør. It lives on the
// clinic (customer.agriaAgentNumber) and is added to the e-mail body when Agria
// is the selected company, so the claim can be matched to the clinic.
function getAgriaAgentNumber(userStore) {
    const customer = (userStore && userStore.customer) || {};
    return String(customer.agriaAgentNumber || '').trim();
}

function buildDefaultBody(companyId, agentNumber) {
    const agentLine = companyId === 'agria' && agentNumber ? `Ombudsnummer: ${agentNumber}\n\n` : '';
    return `Hei,\n\nVedlagt finner dere direkteoppgjørskrav med sykehistorie.\n\n${agentLine}Mvh.`;
}

@observer
class SendInsuranceClaim extends Component {
    constructor(props) {
        super(props);
        const { saleStore, insuranceCompanyStore, userStore } = props.stores;
        const { newSale } = saleStore;
        const firstAnimal = (newSale.animals && newSale.animals[0]) || {};
        const companyId = (firstAnimal.insuranceCompany || '').toLowerCase();
        const company = insuranceCompanyStore.getById(companyId);
        const saleNum = newSale.saleNumber || newSale.id;
        const agentNumber = getAgriaAgentNumber(userStore);

        this.state = {
            companyId: companyId || '',
            companyName: company ? company.name : (firstAnimal.insuranceCompany || ''),
            insuranceId: firstAnimal.insuranceId || '',
            sentTo: company ? company.email : '',
            // Insurers match the claim on the policy number, and Fremtind requires
            // "DIREKTEOPPGJØR + forsikringsnummer" in the subject — include it when known.
            subject: `Direkteoppgjørskrav — ${firstAnimal.name || ''}, sak ${saleNum || ''}${firstAnimal.insuranceId ? `, forsikringsnr ${firstAnimal.insuranceId}` : ''}`,
            body: buildDefaultBody(companyId, agentNumber),
            uploadedFiles: [],
            sending: false,
            sent: false,
            error: null,
            skjemaValues: null,
            skjemaLoading: false,
            // Whether to attach the auto-filled Gjensidige skjema. Flips off
            // automatically when the vet uploads their own filled skjema, so
            // the claim doesn't go out with two versions of the same form.
            includeAutoSkjema: true,
            // TICKET-01651: a samlefaktura stores no animals/journals of its own.
            // The backend resolves them through the sub-sales; until that has
            // loaded the drawer must not claim "no sykehistorie".
            contextAnimal: null,
            contextHasJournals: false,
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.loadCompanies();
        this.loadClaimContext();
        this.maybeLoadSkjema(this.state.companyId);
    }

    // A samlefaktura is built from its visits with the owner and product lines
    // only, so newSale.animals / newSale.journals are empty even though the
    // visits have both. Ask the backend for the resolved context and apply the
    // same defaults the constructor derives from the first animal (TICKET-01651).
    loadClaimContext = async () => {
        const { saleStore, insuranceCompanyStore } = this.props.stores;
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            return;
        }
        const hasSubInvoices = Array.isArray(newSale.subInvoices) && newSale.subInvoices.length > 0;
        const hasAnimals = Array.isArray(newSale.animals) && newSale.animals.filter(Boolean).length > 0;
        if (!hasSubInvoices || hasAnimals) {
            return;
        }
        const response = await util.fetchApi(`/api/sales/${newSale.id}/insurance-claim/context`, { method: 'GET' }, {});
        if (this.mounted === false || !response || response.status !== 200 || !response.data) {
            return;
        }
        const { animals = [], journals = [] } = response.data;
        const contextAnimal = animals.filter(Boolean)[0] || null;
        const contextHasJournals = journals.filter(Boolean).length > 0;
        if (!contextAnimal) {
            this.setState({ contextHasJournals });
            return;
        }
        // The registry may still be loading (see loadCompanies); wait for it so
        // the resolved company gets its name and e-mail address.
        if (insuranceCompanyStore && insuranceCompanyStore.loadAll) {
            await insuranceCompanyStore.loadAll();
            if (this.mounted === false) {
                return;
            }
        }
        const { companyId: currentCompanyId, insuranceId: currentInsuranceId, sentTo: currentSentTo } = this.state;
        // Never clobber a choice the vet already made while the context loaded.
        const companyId = currentCompanyId || (contextAnimal.insuranceCompany || '').toLowerCase();
        const company = insuranceCompanyStore.getById(companyId);
        const insuranceId = currentInsuranceId || contextAnimal.insuranceId || '';
        const saleNum = newSale.saleNumber || newSale.id;
        const { userStore } = this.props.stores;
        this.setState({
            contextAnimal,
            contextHasJournals,
            companyId,
            companyName: company ? company.name : (this.state.companyName || contextAnimal.insuranceCompany || ''),
            insuranceId,
            sentTo: currentSentTo || (company ? company.email : ''),
            subject: `Direkteoppgjørskrav — ${contextAnimal.name || ''}, sak ${saleNum || ''}${insuranceId ? `, forsikringsnr ${insuranceId}` : ''}`,
            body: currentCompanyId ? this.state.body : buildDefaultBody(companyId, getAgriaAgentNumber(userStore)),
        });
        this.maybeLoadSkjema(companyId);
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // The constructor picks selskapsnavn and e-post out of the company registry,
    // but the very first time the drawer is opened in a session that store is
    // still empty — so e-posten til forsikringsselskapet came up blank until the
    // vet closed the drawer and opened it again (TICKET-01462). Re-apply the
    // defaults as soon as the registry has loaded.
    loadCompanies = async () => {
        const { insuranceCompanyStore } = this.props.stores;
        if (!insuranceCompanyStore || !insuranceCompanyStore.loadAll) {
            return;
        }
        await insuranceCompanyStore.loadAll();
        if (this.mounted === false) {
            return;
        }
        const { companyId, sentTo } = this.state;
        const company = insuranceCompanyStore.getById(companyId);
        if (!company) {
            return;
        }
        this.setState({
            companyName: company.name,
            // Never clobber an address the vet typed in the short window between
            // opening the drawer and the registry arriving.
            sentTo: sentTo || company.email || '',
        });
    }

    maybeLoadSkjema = async (companyId) => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        // Only Gjensidige has an auto-filled skjema (BL13_0187).
        if (companyId !== 'gjensidige' || !newSale || !newSale.id) {
            return;
        }
        if (this.state.skjemaValues || this.state.skjemaLoading) {
            return;
        }
        this.setState({ skjemaLoading: true, skjemaError: null });
        const response = await util.fetchApi(`/api/sales/${newSale.id}/insurance-claim/gjensidige-skjema-prefill`, { method: 'GET' }, {});
        if (response && response.status === 200) {
            this.setState({ skjemaValues: response.data || {}, skjemaLoading: false });
        } else {
            this.setState({
                skjemaLoading: false,
                skjemaError: 'Fikk ikke hentet skjemaverdiene fra salget.',
            });
        }
    }

    onClickRetrySkjema = () => {
        this.maybeLoadSkjema(this.state.companyId);
    }

    onSkjemaInput = e => {
        const { name, value } = e.target;
        this.setState(prev => ({ skjemaValues: { ...prev.skjemaValues, [name]: value } }));
    }

    getSkjemaPayload = () => {
        const { skjemaValues, insuranceId } = this.state;
        if (!skjemaValues) {
            return null;
        }
        return {
            ...skjemaValues,
            // The drawer's Forsikrings-ID field is the fallback when the
            // animal had no insuranceId at prefill time.
            forsikringsnummer: skjemaValues.forsikringsnummer || insuranceId || '',
        };
    }

    onChangeCompany = e => {
        const { value } = e.target;
        const { insuranceCompanyStore, userStore } = this.props.stores;
        const company = insuranceCompanyStore.getById(value);
        // 'annet' = the user wants to send to a company that isn't in our list. Start
        // with an empty name so they type the real selskapsnavn, and don't touch the
        // email field (they'll fill it in themselves).
        const isOther = value === 'annet';
        // Re-generate the ombudsnummer line for the new company, but only while the
        // body is still the untouched default so we never clobber the vet's own text.
        const agentNumber = getAgriaAgentNumber(userStore);
        const previousDefault = buildDefaultBody(this.state.companyId, agentNumber);
        const body = this.state.body === previousDefault ? buildDefaultBody(value, agentNumber) : this.state.body;
        this.setState({
            companyId: value,
            companyName: company ? company.name : (isOther ? '' : value),
            // Only overwrite the email when the selected company has a default address.
            // Companies without a known email (e.g. Frende, Annet) keep whatever the user typed.
            sentTo: company && company.email ? company.email : this.state.sentTo,
            body,
        });
        this.maybeLoadSkjema(value);
    }

    handleUploadedFile = (file) => {
        this.setState(prev => ({
            uploadedFiles: [...prev.uploadedFiles, file],
            // The first own upload is usually their own filled skjema — drop
            // the auto-generated one by default (the vet can re-tick it).
            includeAutoSkjema: prev.uploadedFiles.length === 0 ? false : prev.includeAutoSkjema,
        }));
    }

    removeUploadedFile = e => {
        const url = e.target.closest('button').dataset.url;
        this.setState(prev => {
            const uploadedFiles = prev.uploadedFiles.filter(f => f.s3Link !== url);
            return {
                uploadedFiles,
                // Without any own upload, Gjensidige needs the auto skjema again.
                includeAutoSkjema: uploadedFiles.length === 0 ? true : prev.includeAutoSkjema,
            };
        });
    }

    onToggleIncludeAutoSkjema = e => {
        this.setState({ includeAutoSkjema: e.target.checked });
    }

    onClickSend = async () => {
        const { companyId, companyName, insuranceId, sentTo, subject, body, uploadedFiles, includeAutoSkjema } = this.state;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { callback = () => {} } = this.props;

        if (!companyId) {
            this.setState({ error: 'Velg forsikringsselskap (eller "Annet" for å skrive inn manuelt).' });
            return;
        }
        if (companyId === 'annet' && (!companyName || !companyName.trim())) {
            this.setState({ error: 'Skriv inn selskapsnavn.' });
            return;
        }
        if (!sentTo || !sentTo.includes('@')) {
            this.setState({ error: 'Ugyldig e-postadresse.' });
            return;
        }
        if (!newSale || !newSale.id) {
            this.setState({ error: 'Salget må lagres før du kan sende krav.' });
            return;
        }
        // Gjensidige's robot deletes any email without their skjema — the claim
        // must carry either the auto-filled one or the vet's own uploaded form.
        if (companyId === 'gjensidige') {
            if (includeAutoSkjema && !this.state.skjemaValues) {
                this.setState({ error: 'Gjensidige-skjemaet er ikke lastet ennå. Vent et øyeblikk, eller velg selskapet på nytt.' });
                return;
            }
            if (!includeAutoSkjema && uploadedFiles.length === 0) {
                this.setState({ error: 'Gjensidige krever skjemaet: huk av for automatisk utfylt skjema, eller last opp ditt eget som vedlegg.' });
                return;
            }
        }

        this.setState({ sending: true, error: null });

        // 1. Mark krav as sent in the backend FIRST (fast — locks the sale, updates UI).
        //    The email send below is slow (renders PDFs in puppeteer), so we want the
        //    krav state to flip immediately so the user sees the change.
        const claimResponse = await util.fetchApi(
            `/api/sales/${newSale.id}/insurance-claim/send`,
            { method: 'POST' },
            {
                company: companyId,
                companyName,
                insuranceId,
                sentTo,
            },
        );

        if (!claimResponse || claimResponse.status !== 200) {
            this.setState({
                sending: false,
                error: (claimResponse && claimResponse.error) || 'Kunne ikke markere salg som krav-sendt.',
            });
            return;
        }

        saleStore.updateObjectKeyValue('newSale', 'status', 1007);
        saleStore.updateObjectKeyValue('newSale', 'insuranceStatus', 2);
        saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', claimResponse.data && claimResponse.data.insuranceClaim);

        // Reflect the locked state to the user immediately — the email follows below
        // but the user no longer waits to see that the krav has been logged.
        this.setState({ sending: false, sent: true, emailing: true });
        callback(claimResponse);

        // 2. Email via the dedicated send-claim endpoint. The backend generates the
        //    krav-PDF buffer in-process (no JWT in URLs, proper filename) and attaches
        //    it along with sykehistorie (journals on this sale).
        const extraAttachmentUrls = uploadedFiles
            .map(f => f.s3Link)
            .filter(u => typeof u === 'string' && u.trim() && !u.includes('jwtToken='));
        const skjemaPayload = companyId === 'gjensidige' && includeAutoSkjema ? this.getSkjemaPayload() : null;

        try {
            const emailResponse = await util.fetchApi(
                `/api/sales/${newSale.id}/insurance-claim/email`,
                { method: 'POST' },
                {
                    sentTo,
                    subject,
                    body,
                    includeJournals: true,
                    extraAttachmentUrls,
                    ...(skjemaPayload ? { gjensidigeSkjema: skjemaPayload } : {}),
                },
            );
            if (emailResponse && emailResponse.status === 200) {
                // Mirror the attachmentNames the backend just persisted on the
                // claim, so the Forsikring section can show what was sent.
                const attachmentNames = (emailResponse.data && emailResponse.data.attachmentNames) || [];
                const currentClaim = saleStore.newSale.insuranceClaim || {};
                saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', { ...currentClaim, attachmentNames });
                this.setState({ emailing: false });
            } else {
                this.setState({
                    emailing: false,
                    emailError: (emailResponse && emailResponse.error) || 'E-postsending feilet — krav er likevel markert som sendt.',
                });
            }
        } catch (err) {
            this.setState({ emailing: false, emailError: 'E-postsending feilet — krav er likevel markert som sendt.' });
        }
    }

    onClickPreviewPdf = () => {
        const { companyName, insuranceId, sentTo } = this.state;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            this.setState({ error: 'Salget må lagres før du kan forhåndsvise kravet.' });
            return;
        }
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();
        // Before sending, nothing is stored on the sale, so pass the chosen
        // company/ID along so the PDF header shows what will be sent.
        const params = [
            `companyName=${encodeURIComponent(companyName || '')}`,
            `insuranceId=${encodeURIComponent(insuranceId || '')}`,
            `sentTo=${encodeURIComponent(sentTo || '')}`,
        ].join('&');
        window.open(`${apiServer}/api/sales/${newSale.id}/insurance-claim/pdf?${params}&jwtToken=${jwtToken}`, '_blank');
    }

    onClickPreviewSkjema = () => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            this.setState({ error: 'Salget må lagres før du kan forhåndsvise skjemaet.' });
            return;
        }
        const payload = this.getSkjemaPayload() || {};
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();
        // Pass the current (possibly edited) values so the preview matches
        // exactly what will be attached on send.
        const params = Object.keys(payload)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key] != null ? payload[key] : '')}`)
            .join('&');
        window.open(`${apiServer}/api/sales/${newSale.id}/insurance-claim/gjensidige-skjema-pdf?${params}&jwtToken=${jwtToken}`, '_blank');
    }

    onClickPreviewSykehistorie = () => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            this.setState({ error: 'Salget må lagres før du kan forhåndsvise sykehistorien.' });
            return;
        }
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();
        window.open(`${apiServer}/api/sales/${newSale.id}/insurance-claim/sykehistorie-pdf?jwtToken=${jwtToken}`, '_blank');
    }

    render() {
        const { saleStore, insuranceCompanyStore, userStore } = this.props.stores;
        const { newSale } = saleStore;
        const { companies } = insuranceCompanyStore;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const {
            companyId, companyName, insuranceId, sentTo, subject, body,
            uploadedFiles, sending, sent, error, emailing, emailError,
            skjemaValues, skjemaLoading, skjemaError, includeAutoSkjema,
        } = this.state;
        const firstAnimal = (newSale.animals && newSale.animals[0]) || this.state.contextAnimal || {};
        const saleNum = newSale.saleNumber || newSale.id || '';
        const hasJournals = (newSale.journals || []).filter(Boolean).length > 0 || this.state.contextHasJournals;
        // Per-company requirements live in the backend registry (Gjensidige's
        // skjema, Fremtind's subject line). Show them, or the claim gets
        // rejected for a reason the user never saw (TICKET-01417).
        const selectedCompany = insuranceCompanyStore.getById(companyId);
        const companyNotes = (selectedCompany && selectedCompany.notes) || '';
        const agriaAgentNumber = getAgriaAgentNumber(userStore);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        <Text id='dyrejournal.send-insurance-claim'>Send direkteoppgjørskrav</Text>
                    </div>

                    <div class={`mx-3 px-3 py-3 mt-2 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                        <div class='mb-3'>
                            <strong>{firstAnimal.name || ''}</strong>
                            {firstAnimal.species && <span class='text-body-secondary ms-2'>({firstAnimal.species}{firstAnimal.breed ? ` / ${firstAnimal.breed}` : ''})</span>}
                        </div>

                        <label class='form-label'><Text id='input.insuranceCompany'>Forsikringsselskap</Text></label>
                        <select class='form-select mb-3' value={companyId} onChange={this.onChangeCompany}>
                            <option value=''>—</option>
                            {(companies || []).map(c => <option value={c.id} selected={c.id === companyId}>{c.name}</option>)}
                            <option value='annet' selected={companyId === 'annet'}><Text id='dyrejournal.send-insurance-claim.other'>Annet (skriv inn manuelt)</Text></option>
                        </select>

                        {!!companyNotes && (
                            <div class='alert alert-info py-2 px-3 mb-3'>
                                <i class='fa-solid fa-circle-info me-2' />
                                {companyNotes}
                            </div>
                        )}

                        {companyId === 'agria' && (
                            <div class={`alert ${agriaAgentNumber ? 'alert-secondary' : 'alert-warning'} py-2 px-3 mb-3`}>
                                <i class='fa-duotone fa-id-badge me-2' />
                                {agriaAgentNumber ? (
                                    <Text id='dyrejournal.send-insurance-claim.agent-number' fields={{ agentNumber: agriaAgentNumber }}>Ombudsnummer {agriaAgentNumber} (fra klinikkinnstillingene) skrives på kravet og i e-posten.</Text>
                                ) : (
                                    <Text id='dyrejournal.send-insurance-claim.agent-number-missing'>Klinikken har ikke registrert ombudsnummer fra Agria. Legg det inn under Klinikk, feltet «Agria ombudsnummer», så følger det automatisk med kravet.</Text>
                                )}
                            </div>
                        )}

                        {companyId === 'annet' && (
                            <>
                                <label class='form-label'><Text id='input.insuranceCompanyName'>Selskapsnavn</Text></label>
                                <input type='text' class='form-control mb-3' value={companyName} onInput={linkstate(this, 'companyName')} placeholder='F.eks. Tryg, Sparebank 1, Storebrand …' />
                            </>
                        )}

                        {companyId === 'gjensidige' && (
                            <div class='border rounded p-3 mb-3'>
                                <div class='fw-bold'>
                                    <i class='fa-duotone fa-file-signature me-2' />
                                    <Text id='dyrejournal.send-insurance-claim.skjema-title'>Gjensidige-skjema (fylles ut automatisk)</Text>
                                </div>
                                <div class='form-check my-2'>
                                    <input
                                        class='form-check-input'
                                        type='checkbox'
                                        id='includeAutoSkjemaCheck'
                                        checked={includeAutoSkjema}
                                        onChange={this.onToggleIncludeAutoSkjema}
                                    />
                                    <label class='form-check-label' for='includeAutoSkjemaCheck'>
                                        <Text id='dyrejournal.send-insurance-claim.skjema-include'>Legg ved automatisk utfylt Gjensidige-skjema</Text>
                                    </label>
                                </div>
                                {!includeAutoSkjema && (
                                    <small class='text-body-secondary d-block'>
                                        <Text id='dyrejournal.send-insurance-claim.skjema-excluded'>Skjemaet legges ikke ved. Last opp ditt eget utfylte skjema under «Ekstra vedlegg».</Text>
                                    </small>
                                )}
                                {includeAutoSkjema && (
                                <small class='text-body-secondary d-block mb-3'>
                                    <Text id='dyrejournal.send-insurance-claim.skjema-help'>Kontroller feltene før du sender. Beløp skal være hele kroner uten mva, og kun utgifter relatert til diagnosen kravet gjelder.</Text>
                                </small>
                                )}
                                {includeAutoSkjema && skjemaLoading && (
                                    <div class='text-body-secondary'>
                                        <i class='fa-duotone fa-spinner-scale fa-spin me-2' />
                                        <Text id='dyrejournal.send-insurance-claim.skjema-loading'>Henter skjemaverdier fra salget...</Text>
                                    </div>
                                )}
                                {includeAutoSkjema && !skjemaLoading && !skjemaValues && skjemaError && (
                                    <div class='alert alert-warning py-2 px-3 mb-0'>
                                        <i class='fa-solid fa-triangle-exclamation me-2' />
                                        {skjemaError}
                                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill ms-3' onClick={this.onClickRetrySkjema}>
                                            <i class='fa-solid fa-rotate-right me-2' />
                                            <Text id='dyrejournal.send-insurance-claim.skjema-retry'>Prøv igjen</Text>
                                        </button>
                                    </div>
                                )}
                                {includeAutoSkjema && !skjemaLoading && skjemaValues && (
                                    <div class='row'>
                                        {SKJEMA_FIELDS.map(field => (
                                            <div key={field.name} class={`${field.wide ? 'col-12' : 'col-md-6'} mb-2`}>
                                                <label class='form-label mb-0'><small>{field.label}</small></label>
                                                {field.type === 'janei' ? (
                                                    <select class='form-select form-select-sm' name={field.name} value={skjemaValues[field.name]} onChange={this.onSkjemaInput}>
                                                        <option value='nei' selected={skjemaValues[field.name] !== 'ja'}>Nei</option>
                                                        <option value='ja' selected={skjemaValues[field.name] === 'ja'}>Ja</option>
                                                    </select>
                                                ) : (
                                                    <input type='text' class='form-control form-control-sm' name={field.name} value={skjemaValues[field.name]} onInput={this.onSkjemaInput} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <label class='form-label'><Text id='input.insuranceCompanyEmail'>E-post til forsikringsselskap</Text></label>
                        <input type='email' class='form-control mb-3' value={sentTo} onInput={linkstate(this, 'sentTo')} />

                        <label class='form-label'><Text id='input.insuranceId'>Forsikrings-ID</Text></label>
                        <input type='text' class='form-control mb-3' value={insuranceId} onInput={linkstate(this, 'insuranceId')} />

                        <label class='form-label'><Text id='input.subject'>Emne</Text></label>
                        <input type='text' class='form-control mb-3' value={subject} onInput={linkstate(this, 'subject')} />

                        <label class='form-label'><Text id='input.body'>Melding</Text></label>
                        <textarea class='form-control mb-3' rows={5} value={body} onInput={linkstate(this, 'body')} />

                        <label class='form-label'><Text id='dyrejournal.send-insurance-claim.extra-attachments'>Ekstra vedlegg (f.eks. Gjensidige-skjema)</Text></label>
                        <DragNDropFileupload
                            handleAddImage={this.handleUploadedFile}
                            apiUrl={'/api/fileupload'}
                            autoOpen={false}
                            accept='*/*'
                            text={<Text id='dyrejournal.send-insurance-claim.drop'>Slipp filer her eller klikk</Text>}
                            typeOfFile='file'
                        />
                        <div class='mt-3'>
                            <small class='text-body-secondary d-block mb-1'>
                                <Text id='dyrejournal.send-insurance-claim.attachments'>Vedlegg som sendes til forsikringsselskapet:</Text>
                            </small>
                            <div class='d-flex flex-row align-items-center py-1'>
                                <i class='fa-solid fa-file-pdf me-2 text-body-secondary' />
                                <span>krav-{saleNum}.pdf</span>
                                <small class='text-body-secondary ms-2'>
                                    <Text id='dyrejournal.send-insurance-claim.attachment-krav'>(direkteoppgjørskravet)</Text>
                                </small>
                            </div>
                            {hasJournals && (
                                <div class='d-flex flex-row align-items-center py-1'>
                                    <i class='fa-solid fa-file-pdf me-2 text-body-secondary' />
                                    <span>sykehistorie-{saleNum}.pdf</span>
                                    <small class='text-body-secondary ms-2'>
                                        <Text id='dyrejournal.send-insurance-claim.attachment-sykehistorie'>(journalene på dette salget)</Text>
                                    </small>
                                </div>
                            )}
                            {companyId === 'gjensidige' && includeAutoSkjema && skjemaValues && (
                                <div class='d-flex flex-row align-items-center py-1'>
                                    <i class='fa-solid fa-file-pdf me-2 text-body-secondary' />
                                    <span>{`gjensidige-direkteoppgjor-${saleNum}.pdf`}</span>
                                    <small class='text-body-secondary ms-2'>
                                        <Text id='dyrejournal.send-insurance-claim.attachment-skjema'>(Gjensidige-skjemaet, fylles ut automatisk)</Text>
                                    </small>
                                </div>
                            )}
                            {uploadedFiles.map(file => (
                                <div key={file.s3Link} class='d-flex flex-row align-items-center py-1'>
                                    <i class='fa-solid fa-file me-2 text-body-secondary' />
                                    <span>{file.name}</span>
                                    <button type='button' class='btn btn-sm btn-outline-danger ms-2 rounded-pill' data-url={file.s3Link} onClick={this.removeUploadedFile}>
                                        <i class='fa-solid fa-times' />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {error && <div class='alert alert-danger mt-3'>{error}</div>}
                        {emailError && <div class='alert alert-warning mt-3'>{emailError}</div>}

                        <div class='d-flex flex-row align-items-center mt-4'>
                            <button
                                type='button'
                                class={`btn ${sent ? 'btn-success' : 'btn-warning'} rounded-pill`}
                                disabled={sending || sent}
                                onClick={this.onClickSend}
                            >
                                {sending && <i class='fa-duotone fa-spinner-scale me-2' />}
                                {sent ? (
                                    <span><i class='fa-solid fa-check me-2' /><Text id='dyrejournal.send-insurance-claim.sent'>Krav sendt</Text></span>
                                ) : (
                                    <span><i class='fa-solid fa-paper-plane me-2' /><Text id='dyrejournal.send-insurance-claim.send'>Send krav</Text></span>
                                )}
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-secondary rounded-pill ms-3'
                                onClick={this.onClickPreviewPdf}
                            >
                                <i class='fa-solid fa-file-pdf me-2' />
                                <Text id='dyrejournal.send-insurance-claim.preview'>Forhåndsvis krav (PDF)</Text>
                            </button>
                            {hasJournals && (
                                <button
                                    type='button'
                                    class='btn btn-outline-secondary rounded-pill ms-3'
                                    onClick={this.onClickPreviewSykehistorie}
                                >
                                    <i class='fa-solid fa-file-pdf me-2' />
                                    <Text id='dyrejournal.send-insurance-claim.preview-sykehistorie'>Forhåndsvis sykehistorie (PDF)</Text>
                                </button>
                            )}
                            {companyId === 'gjensidige' && includeAutoSkjema && skjemaValues && (
                                <button
                                    type='button'
                                    class='btn btn-outline-secondary rounded-pill ms-3'
                                    onClick={this.onClickPreviewSkjema}
                                >
                                    <i class='fa-solid fa-file-pdf me-2' />
                                    <Text id='dyrejournal.send-insurance-claim.preview-skjema'>Forhåndsvis Gjensidige-skjema (PDF)</Text>
                                </button>
                            )}
                        </div>

                        {emailing && (
                            <div class='mt-3 text-body-secondary'>
                                <small>
                                    <i class='fa-duotone fa-spinner-scale fa-spin me-2' />
                                    <Text id='dyrejournal.send-insurance-claim.emailing'>Sender e-post til forsikringsselskap... (kan ta opptil 30 sekunder)</Text>
                                </small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SendInsuranceClaim;
